import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Trophy, ArrowRight, Star, BookOpen } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

export default function ClubLeaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [stats, setStats] = useState({ total_members: 0, total_books: 0, total_reviews: 0 });

  useEffect(() => {
    api.get('/club/leaderboard').then(r => setLeaders(r.data)).catch(() => {});
    api.get('/club/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SeoHead
        title="Leaderboard — The Authors' Review Exchange | Top Book Reviewers"
        description="See the top book reviewers in The Authors' Review Exchange. Our most active members earn badges and recognition for helping fellow authors get Amazon reviews."
        keywords="book review leaderboard, top book reviewers, review exchange ranking, best book reviewers"
        path="/club/leaderboard"
      />

      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Leaderboard</p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight mb-4" data-testid="leaderboard-title">
              Top <span className="text-primary">Reviewers</span>
            </h1>
            <p className="text-muted-foreground max-w-lg mx-auto text-sm">
              Our most active members helping fellow authors succeed. Review books to climb the ranks and earn badges.
            </p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-12">
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center" data-testid="lb-stat-members">
              <p className="font-serif text-2xl text-primary">{stats.total_members}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Members</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center" data-testid="lb-stat-reviews">
              <p className="font-serif text-2xl text-primary">{stats.total_reviews}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Reviews</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 text-center" data-testid="lb-stat-books">
              <p className="font-serif text-2xl text-primary">{stats.total_books}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Books</p>
            </div>
          </div>

          {/* Badge Legend */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {[
              { name: 'Bronze', color: '#CD7F32', min: 5 },
              { name: 'Silver', color: '#C0C0C0', min: 15 },
              { name: 'Gold', color: '#D4AF37', min: 30 },
              { name: 'Platinum', color: '#E5E4E2', min: 50 },
            ].map(b => (
              <span key={b.name} className="flex items-center gap-1.5 text-xs" style={{ color: b.color }}>
                <Award className="w-3 h-3" /> {b.name} ({b.min}+ reviews)
              </span>
            ))}
          </div>

          {/* Leaderboard Table */}
          {leaders.length === 0 ? (
            <div className="text-center py-16 bg-white/5 border border-white/10 rounded-sm" data-testid="leaderboard-empty">
              <Trophy className="w-10 h-10 text-primary/30 mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No reviewers yet. Be the first!</p>
              <Link to="/club/dashboard" className="text-sm text-primary hover:underline">Join The Exchange</Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="leaderboard-list">
              {leaders.map((l, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-4 p-4 rounded-sm border transition-all ${i === 0 ? 'bg-primary/10 border-primary/30' : i < 3 ? 'bg-white/5 border-white/10' : 'bg-white/[0.02] border-white/5'}`}
                  data-testid={`leader-${i}`}
                >
                  <div className="w-8 text-center">
                    {i === 0 ? <Trophy className="w-5 h-5 text-primary mx-auto" /> : (
                      <span className="font-serif text-lg text-muted-foreground">{l.rank}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-serif text-base truncate">{l.name}</p>
                      {l.badge && (
                        <span className="flex items-center gap-1 text-[10px] font-bold" style={{ color: l.badge.color }}>
                          <Award className="w-3 h-3" /> {l.badge.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm shrink-0">
                    <span className="flex items-center gap-1 text-muted-foreground"><Star className="w-3 h-3" /> {l.total_reviews}</span>
                    <span className="flex items-center gap-1 text-muted-foreground"><BookOpen className="w-3 h-3" /> {l.total_books}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-12">
            <Link
              to="/club/dashboard"
              data-testid="lb-join-cta"
              className="inline-flex items-center gap-2 rounded-sm px-8 py-3 bg-primary text-primary-foreground font-semibold tracking-wide hover:bg-white hover:text-black transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)]"
            >
              Join The Exchange <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
